import debounce from 'lodash/debounce';
import { useEffect } from 'react';
import { VideoDetailsFragment } from '../../graphql/types';
import useTracking from '../../hooks/useTracking';
import {
  EventCategories,
  EventNames,
  EventProfileCardNames,
  EventSubCategories,
} from '../../utils/trackingEvents';

declare global {
  interface Window {
    _wq: unknown[];
  }
}

interface WistiaPlayerProps {
  content: VideoDetailsFragment;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TFunc = (arg1?: any, arg2?: any) => void;

interface TVideo {
  remove: TFunc;
  duration: () => number;
  time: () => number;
  bind: (name: string, cb: TFunc) => void;
}

const getWistiaIdFromUrl = (url: string): string => {
  const { pathname } = new URL(url);
  const paths = pathname.split('/');
  // url is like: https://wistia.com/embed/v1/<wistiaId>.m3u8
  // this will return <wistiaId>.m3u8 -> split by . and return the first element (wistiaId)
  return paths[paths.length - 1].split('.')[0];
};

const WistiaPlayer = ({ content }: WistiaPlayerProps) => {
  const { track } = useTracking();
  const hashedId = getWistiaIdFromUrl(content.video);
  const trackVideoEvent = (eventName: EventNames): void => {
    track(eventName, {
      card_name: EventProfileCardNames.ContentPreviewModal,
      event_category: EventCategories.Content,
      event_subcategory: EventSubCategories.Preview,
      content_id: content.id,
    });
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: eslint migration
  useEffect(() => {
    let handle: TVideo;
    window._wq = window._wq || [];
    window._wq.push({
      id: hashedId,
      onEmbedded: (video: TVideo) => {
        handle = video;

        video.bind('enterfullscreen', () => {
          trackVideoEvent(EventNames.Fullscreen);
        });
        video.bind('cancelfullscreen', () => {
          trackVideoEvent(EventNames.ExitFullscreen);
        });
        video.bind('pause', () => {
          trackVideoEvent(EventNames.Pause);
        });
        video.bind('play', () => {
          trackVideoEvent(EventNames.Play);
        });
        video.bind(
          'seek',
          debounce(() => {
            trackVideoEvent(EventNames.Seek);
          }, 200),
        );
        video.bind('volumechange', (_: number, isMuted: boolean) => {
          if (isMuted) {
            trackVideoEvent(EventNames.Mute);
          } else {
            trackVideoEvent(EventNames.Unmute);
          }
        });
      },
    });
    return function onUnload() {
      if (handle) {
        handle.remove();
      }
      trackVideoEvent(EventNames.Stop);
    };
  }, []);

  return (
    <div
      data-testid="wistiaPlayer"
      className={`wistia_embed wistia_async_${hashedId} videoFoam=true`}
      style={{ height: '720px', width: '100%' }}
    />
  );
};

export default WistiaPlayer;
